import { styled } from '@mui/material';
import { green } from '@mui/material/colors';
import React, { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  EmailHeaderProps,
  ConfidencePillProps,
  SectionBaseProps,
  EmailRowsProps,
  EmailRowProps,
} from '@/reports/contact/Interfaces';
import { RowCard } from '@/reports/contact/RowCard';
import { ShowMoreToggle } from '@/reports/contact/components';
import {
  classifyEmails,
  calculateConfidencePercentage,
} from '@/reports/contact/utils';
import {
  ReportSection,
  RestoreAllDataButton,
  openReportInNewTab,
  WarningFooter,
} from '@/reports/index';
import { FlexContainer } from '@/reports/shared/OverviewItems/components';
import { commonTypes } from '@/reports/types';
import { Icons, Pill, Stack, Text, useTheme } from '@/theme/index';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';

export interface ContactEmailSectionProps extends SectionBaseProps {
  emailsCount: number;
  emailList: commonTypes.Emails;
}

export const PillContainer = styled(FlexContainer)(({ theme }) => ({
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.secondary.light,
  borderRadius: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightBold,
  padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
}));

export const HeaderContainer = styled(FlexContainer)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
  paddingBottom: theme.spacing(2),

  '.MuiChip-root, .MuiChip-icon': {
    marginRight: theme.spacing(0.25),
  },

  [theme.breakpoints.down('sm')]: {
    '.MuiChip-root, .MuiChip-icon': {
      marginRight: theme.spacing(0.6),
    },
  },
}));

export const DataContainer = styled(FlexContainer)(() => ({
  alignItems: 'flex-start',
}));

export const TextContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.25),
  },
}));

export const HeaderTextContainer = styled(Text)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginLeft: theme.spacing(0.5),
  fontSize: theme.typography.subtitle1.fontSize,

  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(1.25),
  },
}));

const EmailThreshold = 3;
const confidenceColorThreshold = 75;

/**
 * This component shows the % of confidence based on an email address.
 */
function ConfidencePill({ email }: ConfidencePillProps) {
  const theme = useTheme();

  if (email?.is_derived && email?.meta?.confidence) {
    const confidencePercentage = calculateConfidencePercentage(
      email?.meta?.confidence
    );

    return (
      <PillContainer
        sx={{
          color:
            confidencePercentage >= confidenceColorThreshold
              ? green.A700
              : theme.palette.primary.dark,
        }}
      >
        {confidencePercentage}%
      </PillContainer>
    );
  }

  return null;
}

/**
 * This component displays the amount of emails that are shown in the report per category e.g (Professional, Derived, Personal).
 */
function EmailHeader({ title, emailsCount, icon }: EmailHeaderProps) {
  const theme = useTheme();

  if (emailsCount > 0) {
    return (
      <HeaderContainer>
        <Pill
          icon={icon}
          className="badge"
          sx={{
            width: '5%',
            backgroundColor: theme.palette.primary.contrastText,
            flexDirection: 'column',
          }}
        />
        <HeaderTextContainer>
          {title} ({emailsCount})
        </HeaderTextContainer>
      </HeaderContainer>
    );
  }

  return null;
}

/**
 * This component displays an email address individually per category e.g (Professional, Derived, Personal).
 */
function EmailRow({ permalink, email, eventCategory }: EmailRowProps) {
  const { trackEvent } = useAppConfigContext();
  const theme = useTheme();
  const {
    links: { baseUrl },
  } = useAppConstantsContext();
  const openReport = () => {
    trackEvent(`${eventCategory} email`, 'engagement click', 'contact report');
    openReportInNewTab(
      {
        email: email.address || '',
        searchType: 'email',
      },
      baseUrl
    );
  };

  return (
    <RowCard
      permalink={permalink}
      item={email}
      value={email?.address?.toLowerCase() || ''}
      component={
        <DataContainer>
          <TextContainer>
            <Stack
              spacing={1}
              direction="column"
              alignItems="left"
              sx={(theme) => ({
                mr: theme.spacing(0.5),
              })}
            >
              <Text
                color={(theme) => theme.palette.primary.main}
                sx={{ cursor: 'pointer' }}
                onClick={openReport}
              >
                {email?.address?.toLowerCase()}
              </Text>
              {email?.meta?.last_seen_date?.full && (
                <Text fontSize={theme.typography.caption.fontSize}>
                  Last seen:{' '}
                  {formatDateFromReport(email?.meta?.last_seen_date?.full)}
                </Text>
              )}
            </Stack>
          </TextContainer>

          <ConfidencePill email={email} />
        </DataContainer>
      }
    />
  );
}

/**
 * This component displays a list of the email addresses that are shown in the report per category e.g (Professional, Derived, Personal).
 */
function EmailRows({
  permalink,
  emailList,
  eventCategory,
}: EmailRowsProps): JSX.Element {
  return (
    <>
      {emailList.map((email) => (
        <EmailRow
          key={email.address}
          permalink={permalink}
          email={email}
          eventCategory={eventCategory}
        />
      ))}
    </>
  );
}

function emailTypeList(
  expand: boolean,
  emaiList: commonTypes.Email[]
): commonTypes.Email[] {
  return expand ? emaiList : emaiList.slice(0, EmailThreshold);
}

/**
 * This component renders the Contact Email section of the report.
 */
export function ContactEmailSection({
  emailsCount,
  personName,
  emailList,
  permalink,
}: ContactEmailSectionProps) {
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { notDerived, derived, personal } = classifyEmails(emailList);

  // Show More Setters
  const [expandProfessionalEmails, setExpandProfessionalEmails] =
    useState(false);
  const [expandDerivedEmails, setExpandDerivedEmails] = useState(false);
  const [expandPersonalEmails, setExpandPersonalEmails] = useState(false);

  const expandPersonalEmailsHandler = () => {
    setExpandPersonalEmails(!expandPersonalEmails);
  };

  const expandProfessionalEmailsHandler = () => {
    setExpandProfessionalEmails(!expandProfessionalEmails);
  };

  const expandDerivedEmailsHandler = () => {
    setExpandDerivedEmails(!expandDerivedEmails);
  };

  const professionalEmails = emailTypeList(
    expandProfessionalEmails,
    notDerived
  );
  const derivedEmails = emailTypeList(expandDerivedEmails, derived);
  const personalEmails = emailTypeList(expandPersonalEmails, personal);

  return (
    <ReportSection
      title="Possible Emails"
      sectionDescription={`Email addresses  ${personName} may currently or previously have used are displayed below. Run an email report on a particular email address to search for more information.`}
      sectionId="email-section"
      hasContent={emailsCount > 0}
      personName={personName}
      FooterComponent={<WarningFooter />}
    >
      <EmailHeader
        title="Professional Emails"
        emailsCount={notDerived.length}
        icon={<Icons.Verified />}
      />

      <EmailRows
        permalink={permalink}
        emailList={professionalEmails}
        eventCategory="select"
      />

      <ShowMoreToggle
        list={notDerived}
        expand={expandProfessionalEmails}
        expandHandler={expandProfessionalEmailsHandler}
      />

      <EmailHeader
        title={`${brandName} AI`}
        emailsCount={derived.length}
        icon={<Icons.Hub />}
      />

      <EmailRows
        permalink={permalink}
        emailList={derivedEmails}
        eventCategory="derived"
      />

      <ShowMoreToggle
        list={derived}
        expand={expandDerivedEmails}
        expandHandler={expandDerivedEmailsHandler}
      />

      <EmailHeader
        title="Personal Emails"
        emailsCount={personal.length}
        icon={<Icons.Person />}
      />

      <EmailRows
        permalink={permalink}
        emailList={personalEmails}
        eventCategory="personal"
      />

      <ShowMoreToggle
        list={personal}
        expand={expandPersonalEmails}
        expandHandler={expandPersonalEmailsHandler}
      />

      <RestoreAllDataButton items={emailList} permalink={permalink} />
    </ReportSection>
  );
}
